import React, { useEffect, useState } from 'react';
import { fetchTenantModules, setTenantModule, ModuleKey, TenantModules as Modules } from '../../services/modulesApi';
import { confirmAction } from '../../utils/confirm';
import { ui } from '../../styles/shared';

/**
 * Huvudadmin slår på och av moduler för en kund. En avstängd modul försvinner ur kundens
 * meny, dess bakgrundsjobb stannar och data ligger kvar tills den slås på igen.
 */

const ORDER: ModuleKey[] = ['device_schedules', 'inspections', 'heatpump', 'shelly'];

/** Vad som händer när modulen stängs av; visas i bekräftelsen. */
const OFF_EFFECT: Record<ModuleKey, string> = {
    device_schedules: 'Inga tillfällen körs och inget larmar. Det går bara när inga tillfällen är planerade.',
    inspections: 'Påminnelser och eskalering upphör, och länkarna i mejlen slutar fungera. Modulavgiften räknas inte.',
    heatpump: 'Värmepumparna stängs av: de hämtas inte, larmar inte, syns inte på översikten och kostar inte. De som var påslagna slås på igen med modulen.',
    shelly: 'Shelly-enheterna stängs av: de hämtas inte, larmar inte, syns inte på översikten och kostar inte. De som var påslagna slås på igen med modulen.',
};

const HINT: Record<ModuleKey, string> = {
    device_schedules: 'Slå på och av enheter vid bestämda tillfällen',
    inspections: 'Egenkontroller och ronder (modulavgift)',
    heatpump: 'Värmepumpar via myUplink',
    shelly: 'Enheter via Shelly Cloud',
};

const TenantModules: React.FC<{ tenantId: string; tenantName: string }> = ({ tenantId, tenantName }) => {
    const [data, setData] = useState<Modules | null>(null);
    const [busy, setBusy] = useState<ModuleKey | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!tenantId) return;
        setData(null);
        setError(null);
        fetchTenantModules(tenantId).then(setData).catch((e: any) => setError(e.message));
    }, [tenantId]);

    const toggle = async (key: ModuleKey, enabled: boolean) => {
        const label = data?.labels[key] ?? key;
        const question = enabled
            ? `Slå på ${label} för ${tenantName}?`
            : `Stänga av ${label} för ${tenantName}?\n\n${OFF_EFFECT[key]}\n\nInget raderas; allt kommer tillbaka när modulen slås på igen.`;
        if (!confirmAction(question)) return;
        setBusy(key);
        setError(null);
        try {
            setData(await setTenantModule(tenantId, key, enabled));
        } catch (e: any) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div style={{ ...ui.card }} className="no-print">
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Moduler för {tenantName}</h3>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
                Avstängda moduler syns inte i kundens meny och deras bakgrundsjobb körs inte.
            </div>
            {error && <div style={ui.errorBox}><span>{error}</span></div>}
            {!data && !error && <div style={{ color: '#888', fontSize: 13 }}>Hämtar…</div>}
            {data && ORDER.map(key => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: busy ? 'wait' : 'pointer' }}>
                    <input type="checkbox" checked={data.modules[key]} disabled={busy !== null}
                        onChange={e => toggle(key, e.target.checked)} />
                    <span style={{ fontWeight: 600, minWidth: 170 }}>{data.labels[key]}</span>
                    <span style={{ fontSize: 13, color: '#777' }}>{HINT[key]}</span>
                </label>
            ))}
        </div>
    );
};

export default TenantModules;
